<template>
  <div class="track-page">
    <!-- 顶部搜索栏 -->
    <div class="search-bar">
      <div class="search-inner">
        <div class="search-item">
          <span class="label">车牌号</span>
          <el-input
            v-model="searchForm.plateNumber"
            placeholder="请输入车牌号"
            clearable
            size="small"
            style="width: 140px"
            @keyup.enter.native="handleSearch"
          />
        </div>
        <div class="search-item">
          <span class="label">时间范围</span>
          <DatePicker
            v-model="timeRange"
            type="datetimerange"
            format="yyyy-MM-dd HH:mm:ss"
            placeholder="请选择时间范围"
            separator=" 至 "
            style="width: 340px"
            @on-change="handleTimeChange"
          />
        </div>
        <el-button type="primary" size="small" @click="handleSearch" :loading="loading">
          查询
        </el-button>
      </div>
    </div>

    <!-- 主内容区 -->
    <div class="main-content">
      <!-- 左侧控制面板 -->
      <div class="left-panel" v-if="trackData">
        <!-- 基本信息 -->
        <div class="panel-section">
          <div class="panel-header">
            <i class="el-icon-data-line"></i>
            <span>轨迹概况</span>
          </div>
          <div class="stats-row">
            <div class="stat-item">
              <span class="stat-value">{{ trackData.plateNumber }}</span>
              <span class="stat-label">车牌</span>
            </div>
            <div class="stat-item">
              <span class="stat-value">{{ trackData.totalPoints }}</span>
              <span class="stat-label">轨迹点</span>
            </div>
          </div>
        </div>

        <!-- 播放控制 -->
        <div class="panel-section playback-section">
          <div class="panel-header">
            <i class="el-icon-video-play"></i>
            <span>轨迹回放</span>
            <span class="playback-status" :class="{ playing: isPlaying }">
              {{ isPlaying ? '播放中' : '已暂停' }}
            </span>
          </div>
          
          <!-- 主控制按钮 -->
          <div class="main-controls">
            <div 
              class="play-btn" 
              :class="{ playing: isPlaying }"
              @click="togglePlay"
            >
              <i :class="isPlaying ? 'el-icon-video-pause' : 'el-icon-caret-right'"></i>
            </div>
            <div class="control-btn" @click="stopPlay" title="重置">
              <i class="el-icon-refresh-left"></i>
            </div>
          </div>

          <!-- 进度条 -->
          <div class="progress-wrapper">
            <div class="progress-track">
              <div 
                class="progress-fill" 
                :style="{ width: progressPercent + '%' }"
              ></div>
              <input 
                type="range" 
                class="progress-slider"
                :value="currentIndex"
                :max="trackData.pathPoints.length - 1 || 0"
                @input="seekTo($event.target.value)"
              />
            </div>
            <div class="progress-info">
              <span class="progress-current">{{ currentIndex + 1 }}</span>
              <span class="progress-divider">/</span>
              <span class="progress-total">{{ trackData.pathPoints.length }}</span>
            </div>
          </div>

          <!-- 速度选择 -->
          <div class="speed-section">
            <span class="speed-label">播放速度</span>
            <div class="speed-options">
              <div 
                v-for="speed in [1, 2, 4, 8]" 
                :key="speed"
                class="speed-chip"
                :class="{ active: playSpeed === speed }"
                @click="playSpeed = speed"
              >
                {{ speed }}x
              </div>
            </div>
          </div>

          <!-- 当前点信息 -->
          <div class="current-info-card" v-if="currentPointInfo">
            <div class="info-item location">
              <div class="info-icon">
                <i class="el-icon-location"></i>
              </div>
              <div class="info-text">{{ currentPointInfo.location || '未知位置' }}</div>
            </div>
          </div>
        </div>

        <!-- 当前轨迹记录列表 -->
        <div class="panel-section track-list-section" v-if="playedPoints.length > 0">
          <div class="panel-header">
            <i class="el-icon-tickets"></i>
            <span>当前轨迹记录</span>
            <span class="count">{{ playedPoints.length }} 条</span>
          </div>
          <div class="track-list-body" ref="trackListBody">
            <div 
              class="track-item" 
              v-for="item in playedPoints" 
              :key="item._index"
              :class="{ active: item._index === playedPoints.length - 1 }"
            >
              <div class="item-index">{{ item._index + 1 }}</div>
              <div class="item-content">
                <div class="item-main">
                  <span class="item-time">{{ formatFullTime(item.gpsTime) }}</span>
                </div>
                <div class="item-location">{{ item.location || '未知位置' }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 空状态面板 -->
      <div class="left-panel empty" v-else>
        <div class="empty-icon">
          <i class="el-icon-map-location"></i>
        </div>
        <p>输入车牌号查询轨迹</p>
      </div>

      <!-- 右侧地图区域 -->
      <div class="map-area">
        <div id="bmap-container" class="map-container"></div>
      </div>
    </div>
  </div>
</template>

<script>
import { getGpsTrack } from "@/api/index";

export default {
  name: "gpsTrack",
  data() {
    return {
      loading: false,
      searchForm: {
        plateNumber: "",
        startTime: "",
        endTime: ""
      },
      timeRange: [],
      trackData: null,
      map: null,
      polyline: null,
      startMarker: null,
      endMarker: null,
      carMarker: null,
      carInfoLabel: null,
      isPlaying: false,
      playSpeed: 2, // 默认2倍速
      currentIndex: 0,
      playTimer: null,
      currentPointInfo: null,
      playedPoints: []
    };
  },
  computed: {
    progressPercent() {
      if (!this.trackData || !this.trackData.pathPoints || this.trackData.pathPoints.length === 0) {
        return 0;
      }
      return (this.currentIndex / (this.trackData.pathPoints.length - 1)) * 100;
    }
  },
  watch: {
    currentIndex(val) {
      if (this.trackData && this.trackData.pathPoints) {
        this.playedPoints = this.trackData.pathPoints.slice(0, val + 1).map((item, idx) => ({
          ...item,
          _index: idx
        }));
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      }
    }
  },
  mounted() {
    this.initMap();
    this.setDefaultTimeRange();
  },
  beforeDestroy() {
    this.stopPlay();
    if (this.map) {
      this.map = null;
    }
  },
  methods: {
    setDefaultTimeRange() {
      const now = new Date();
      const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000); // 一周前
      this.timeRange = [oneWeekAgo, now];
      this.searchForm.startTime = this.formatDateTime(oneWeekAgo);
      this.searchForm.endTime = this.formatDateTime(now);
    },
    
    // 处理时间范围变化
    handleTimeChange(dateArr) {
      if (dateArr && dateArr.length === 2 && dateArr[0] && dateArr[1]) {
        this.searchForm.startTime = dateArr[0];
        this.searchForm.endTime = dateArr[1];
      } else {
        this.searchForm.startTime = "";
        this.searchForm.endTime = "";
      }
    },
    formatDateTime(date) {
      const pad = (n) => n.toString().padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    },
    formatTime(time) {
      if (!time) return '-';
      if (typeof time === 'string') return time.substring(5, 16).replace(' ', ' ');
      return this.formatDateTime(new Date(time)).substring(5, 16);
    },
    formatPointTime(time) {
      if (!time) return '-';
      if (typeof time === 'string') return time.substring(0, 10);
      return this.formatDateTime(new Date(time)).substring(0, 10);
    },
    formatFullTime(time) {
      if (!time) return '-';
      if (typeof time === 'string') return time.substring(5, 19); // MM-dd HH:mm:ss
      return this.formatDateTime(new Date(time)).substring(5, 19);
    },

    scrollToBottom() {
      const listBody = this.$refs.trackListBody;
      if (listBody) {
        listBody.scrollTop = listBody.scrollHeight;
      }
    },

    initMap() {
      if (typeof BMap === 'undefined') {
        this.loadBMapScript();
        return;
      }
      this.createMap();
    },

    loadBMapScript() {
      const ak = 'ZDFxvPDYGjSZ5LMdDby9hgnqJdD0HZqQ';
      const script = document.createElement('script');
      script.src = `https://api.map.baidu.com/api?v=3.0&ak=${ak}&callback=initBMapCallback`;
      document.head.appendChild(script);
      window.initBMapCallback = () => {
        this.createMap();
      };
    },

    createMap() {
      this.map = new BMap.Map("bmap-container");
      const point = new BMap.Point(119.2, 34.6);
      this.map.centerAndZoom(point, 12);
      this.map.enableScrollWheelZoom(true);
      this.map.addControl(new BMap.NavigationControl({ anchor: BMAP_ANCHOR_TOP_RIGHT }));
      this.map.addControl(new BMap.ScaleControl());
    },

    handleSearch() {
      if (!this.searchForm.plateNumber) {
        this.$message.warning("请输入车牌号");
        return;
      }
      
      this.loading = true;
      this.stopPlay();
      this.playedPoints = [];
      
      getGpsTrack(this.searchForm).then(res => {
        this.loading = false;
        if (res.success && res.result) {
          this.trackData = res.result;
          this.currentIndex = 0;
          this.playedPoints = [{ ...res.result.pathPoints[0], _index: 0 }];
          this.drawTrack();
          this.$message.success(`查询成功，共 ${res.result.totalPoints} 个轨迹点`);
        } else {
          this.$message.warning(res.message || "未查询到轨迹数据");
          this.trackData = null;
          this.playedPoints = [];
          this.clearMap();
        }
      }).catch(() => {
        this.loading = false;
        this.$message.error("查询失败");
      });
    },

    clearMap() {
      if (!this.map) return;
      this.map.clearOverlays();
      this.polyline = null;
      this.startMarker = null;
      this.endMarker = null;
      this.carMarker = null;
      this.carInfoLabel = null;
    },

    // 生成信息窗口内容 - 精美样式
    buildInfoContent(point, type = '') {
      const typeColors = {
        '起点': '#52c41a',
        '终点': '#f5222d',
        '当前位置': '#1890ff',
        '': '#1890ff'
      };
      const color = typeColors[type] || '#1890ff';
      const icon = type === '起点' ? '🚩' : type === '终点' ? '🏁' : type === '当前位置' ? '🚗' : '📍';
      
      return `
        <div style="padding: 0; font-size: 13px; min-width: 240px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
          <div style="background: linear-gradient(135deg, ${color} 0%, ${color}dd 100%); color: #fff; padding: 12px 16px; border-radius: 8px 8px 0 0; font-weight: 600; font-size: 14px;">
            ${icon} ${type || '轨迹点详情'}
          </div>
          <div style="padding: 12px 16px; background: #fff; border-radius: 0 0 8px 8px;">
            <div style="display: flex; margin-bottom: 8px; padding-bottom: 8px; border-bottom: 1px dashed #eee;">
              <span style="color: #999; width: 60px;">时间</span>
              <span style="color: #333; font-weight: 500;">${point.gpsTime || '-'}</span>
            </div>
            <div style="display: flex; margin-bottom: 8px;">
              <span style="color: #999; width: 60px;">位置</span>
              <span style="color: #333; flex: 1;">${point.location || '未知位置'}</span>
            </div>
            <div style="display: flex; margin-bottom: 8px;">
              <span style="color: #999; width: 60px;">坐标</span>
              <span style="color: #666; font-family: monospace; font-size: 12px;">${point.lng?.toFixed(6) || '-'}, ${point.lat?.toFixed(6) || '-'}</span>
            </div>
            <div style="display: flex; margin-bottom: 8px;">
              <span style="color: #999; width: 60px;">方向</span>
              <span style="color: #333;">${point.direction || '-'}°</span>
            </div>
            <div style="display: flex;">
              <span style="color: #999; width: 60px;">状态</span>
              <span style="color: #333;">${point.status || '-'}</span>
            </div>
          </div>
        </div>
      `;
    },

    // ========== 坐标转换函数 ==========
    // WGS-84 转 GCJ-02（火星坐标）
    wgs84ToGcj02(lng, lat) {
      const PI = Math.PI;
      const a = 6378245.0;
      const ee = 0.00669342162296594323;
      
      if (lng < 72.004 || lng > 137.8347 || lat < 0.8293 || lat > 55.8271) {
        return { lng, lat };
      }
      
      let dLat = this.transformLat(lng - 105.0, lat - 35.0);
      let dLng = this.transformLng(lng - 105.0, lat - 35.0);
      const radLat = lat / 180.0 * PI;
      let magic = Math.sin(radLat);
      magic = 1 - ee * magic * magic;
      const sqrtMagic = Math.sqrt(magic);
      dLat = (dLat * 180.0) / ((a * (1 - ee)) / (magic * sqrtMagic) * PI);
      dLng = (dLng * 180.0) / (a / sqrtMagic * Math.cos(radLat) * PI);
      
      return { lng: lng + dLng, lat: lat + dLat };
    },
    
    gcj02ToBd09(lng, lat) {
      const PI = Math.PI;
      const z = Math.sqrt(lng * lng + lat * lat) + 0.00002 * Math.sin(lat * PI * 3000.0 / 180.0);
      const theta = Math.atan2(lat, lng) + 0.000003 * Math.cos(lng * PI * 3000.0 / 180.0);
      return { lng: z * Math.cos(theta) + 0.0065, lat: z * Math.sin(theta) + 0.006 };
    },
    
    wgs84ToBd09(lng, lat) {
      const gcj02 = this.wgs84ToGcj02(lng, lat);
      return this.gcj02ToBd09(gcj02.lng, gcj02.lat);
    },
    
    transformLat(lng, lat) {
      const PI = Math.PI;
      let ret = -100.0 + 2.0 * lng + 3.0 * lat + 0.2 * lat * lat + 0.1 * lng * lat + 0.2 * Math.sqrt(Math.abs(lng));
      ret += (20.0 * Math.sin(6.0 * lng * PI) + 20.0 * Math.sin(2.0 * lng * PI)) * 2.0 / 3.0;
      ret += (20.0 * Math.sin(lat * PI) + 40.0 * Math.sin(lat / 3.0 * PI)) * 2.0 / 3.0;
      ret += (160.0 * Math.sin(lat / 12.0 * PI) + 320 * Math.sin(lat * PI / 30.0)) * 2.0 / 3.0;
      return ret;
    },
    
    transformLng(lng, lat) {
      const PI = Math.PI;
      let ret = 300.0 + lng + 2.0 * lat + 0.1 * lng * lng + 0.1 * lng * lat + 0.1 * Math.sqrt(Math.abs(lng));
      ret += (20.0 * Math.sin(6.0 * lng * PI) + 20.0 * Math.sin(2.0 * lng * PI)) * 2.0 / 3.0;
      ret += (20.0 * Math.sin(lng * PI) + 40.0 * Math.sin(lng / 3.0 * PI)) * 2.0 / 3.0;
      ret += (150.0 * Math.sin(lng / 12.0 * PI) + 300.0 * Math.sin(lng / 30.0 * PI)) * 2.0 / 3.0;
      return ret;
    },

    drawTrack() {
      if (!this.map || !this.trackData || !this.trackData.pathPoints) return;
      this.clearMap();

      const pathPoints = this.trackData.pathPoints;
      if (pathPoints.length === 0) return;

      // 坐标转换
      const points = pathPoints.map(p => {
        const bd09 = this.wgs84ToBd09(p.lng, p.lat);
        return new BMap.Point(bd09.lng, bd09.lat);
      });
      const self = this;

      // 轨迹线
      this.polyline = new BMap.Polyline(points, {
        strokeColor: "#1890ff",
        strokeWeight: 4,
        strokeOpacity: 0.8
      });
      this.map.addOverlay(this.polyline);

      // 轨迹点标记（每隔N个点显示一个小圆点，仅展示不可点击）
      const step = Math.max(1, Math.floor(pathPoints.length / 30));
      pathPoints.forEach((p, index) => {
        if (index > 0 && index < pathPoints.length - 1 && index % step !== 0) return;
        
        const bd09 = this.wgs84ToBd09(p.lng, p.lat);
        const point = new BMap.Point(bd09.lng, bd09.lat);
        
        const circle = new BMap.Circle(point, 5, {
          strokeColor: "#1890ff",
          strokeWeight: 2,
          strokeOpacity: 0.8,
          fillColor: "#fff",
          fillOpacity: 1,
          enableClicking: false // 禁止点击
        });
        
        this.map.addOverlay(circle);
      });

      // 起点标记（可点击）
      const startPointData = pathPoints[0];
      const startTime = startPointData.gpsTime ? startPointData.gpsTime.substring(5, 16) : '';
      const startLabel = new BMap.Label(`
        <div style="display: flex; align-items: center; gap: 6px; cursor: pointer;">
          <div style="
            width: 28px; height: 28px;
            background: linear-gradient(135deg, #52c41a 0%, #73d13d 100%);
            border-radius: 50%;
            display: flex; align-items: center; justify-content: center;
            color: #fff; font-size: 13px; font-weight: bold;
            border: 2px solid #fff;
            box-shadow: 0 2px 8px rgba(82,196,26,0.4);
            transition: transform 0.2s;
          ">起</div>
          <div style="
            background: rgba(255,255,255,0.95);
            padding: 4px 10px;
            border-radius: 12px;
            font-size: 11px;
            color: #52c41a;
            font-weight: 500;
            box-shadow: 0 2px 6px rgba(0,0,0,0.1);
          ">${startTime}</div>
        </div>
      `, {
        position: points[0],
        offset: new BMap.Size(-14, -14)
      });
      startLabel.setStyle({
        padding: "0",
        backgroundColor: "transparent",
        border: "none",
        cursor: "pointer"
      });
      startLabel.addEventListener("click", () => {
        this.showPointInfoWindow(startPointData, points[0], '起点', '#52c41a');
      });
      this.map.addOverlay(startLabel);

      // 终点标记（可点击）
      const endPointData = pathPoints[pathPoints.length - 1];
      const endPoint = points[points.length - 1];
      const endTime = endPointData.gpsTime ? endPointData.gpsTime.substring(5, 16) : '';
      const endLabel = new BMap.Label(`
        <div style="display: flex; align-items: center; gap: 6px; cursor: pointer;">
          <div style="
            width: 28px; height: 28px;
            background: linear-gradient(135deg, #f5222d 0%, #ff7875 100%);
            border-radius: 50%;
            display: flex; align-items: center; justify-content: center;
            color: #fff; font-size: 13px; font-weight: bold;
            border: 2px solid #fff;
            box-shadow: 0 2px 8px rgba(245,34,45,0.4);
            transition: transform 0.2s;
          ">终</div>
          <div style="
            background: rgba(255,255,255,0.95);
            padding: 4px 10px;
            border-radius: 12px;
            font-size: 11px;
            color: #f5222d;
            font-weight: 500;
            box-shadow: 0 2px 6px rgba(0,0,0,0.1);
          ">${endTime}</div>
        </div>
      `, {
        position: endPoint,
        offset: new BMap.Size(-14, -14)
      });
      endLabel.setStyle({
        padding: "0",
        backgroundColor: "transparent",
        border: "none",
        cursor: "pointer"
      });
      endLabel.addEventListener("click", () => {
        this.showPointInfoWindow(endPointData, endPoint, '终点', '#f5222d');
      });
      this.map.addOverlay(endLabel);

      // 车辆图标
      const carIcon = new BMap.Icon(
        "https://api.map.baidu.com/images/marker_red_sprite.png",
        new BMap.Size(24, 24),
        { anchor: new BMap.Size(12, 24) }
      );
      this.carMarker = new BMap.Marker(points[0], { enableClicking: false });
      this.carMarker.setIcon(carIcon);
      this.map.addOverlay(this.carMarker);

      // 定位到起点，缩放级别15
      this.map.centerAndZoom(points[0], 15);
      this.updateCurrentPointInfo();
      
      // 默认显示轨迹点详情
      const firstPoint = pathPoints[0];
      const firstBd09 = this.wgs84ToBd09(firstPoint.lng, firstPoint.lat);
      const firstBmapPoint = new BMap.Point(firstBd09.lng, firstBd09.lat);
      this.updateCarInfoWindow(firstPoint, firstBmapPoint);
    },

    togglePlay() {
      if (this.isPlaying) {
        this.pausePlay();
      } else {
        this.startPlay();
      }
    },

    startPlay() {
      if (!this.trackData || !this.trackData.pathPoints) return;
      
      const points = this.trackData.pathPoints;
      if (this.currentIndex >= points.length - 1) {
        this.currentIndex = 0;
        this.playedPoints = [{ ...points[0], _index: 0 }];
      }

      this.isPlaying = true;
      this.playStep();
    },

    playStep() {
      if (!this.isPlaying) return;
      
      const points = this.trackData.pathPoints;
      if (this.currentIndex >= points.length - 1) {
        this.isPlaying = false;
        // 播放完成时保持信息标签显示
        this.$message.success("播放完成");
        return;
      }

      this.currentIndex++;
      this.moveCarToIndex(this.currentIndex);

      const delay = 500 / this.playSpeed;
      this.playTimer = setTimeout(() => {
        this.playStep();
      }, delay);
    },

    pausePlay() {
      this.isPlaying = false;
      if (this.playTimer) {
        clearTimeout(this.playTimer);
        this.playTimer = null;
      }
      // 暂停时保持信息标签显示
    },

    stopPlay() {
      this.pausePlay();
      this.currentIndex = 0;
      if (this.trackData && this.trackData.pathPoints && this.trackData.pathPoints.length > 0) {
        this.playedPoints = [{ ...this.trackData.pathPoints[0], _index: 0 }];
        const point = this.trackData.pathPoints[0];
        const bd09 = this.wgs84ToBd09(point.lng, point.lat);
        const bmapPoint = new BMap.Point(bd09.lng, bd09.lat);
        if (this.carMarker) {
          this.carMarker.setPosition(bmapPoint);
          this.map.centerAndZoom(bmapPoint, 15);
        }
        this.updateCurrentPointInfo();
        // 重置后显示第一个点的信息
        this.updateCarInfoWindow(point, bmapPoint);
      }
    },

    moveCarToIndex(index) {
      if (!this.carMarker || !this.trackData || !this.trackData.pathPoints) return;
      
      const points = this.trackData.pathPoints;
      if (index < 0 || index >= points.length) return;

      const point = points[index];
      const bd09 = this.wgs84ToBd09(point.lng, point.lat);
      const bmapPoint = new BMap.Point(bd09.lng, bd09.lat);
      this.carMarker.setPosition(bmapPoint);
      
      // 地图跟随车辆移动
      this.map.panTo(bmapPoint);
      
      this.updateCurrentPointInfo();
      this.updateCarInfoWindow(point, bmapPoint);
    },
    
    updateCarInfoWindow(point, bmapPoint) {
      if (!this.map) return;
      
      const dateTime = point.gpsTime ? point.gpsTime.substring(0, 19) : '-';
      const location = point.location || '未知位置';
      const lng = point.lng ? point.lng.toFixed(6) : '-';
      const lat = point.lat ? point.lat.toFixed(6) : '-';
      const currentIdx = this.currentIndex + 1;
      const totalCount = this.trackData?.pathPoints?.length || 0;
      const direction = point.direction || 0;
      const directionText = this.getDirectionText(direction);
      const status = point.status || '-';
      
      if (!this.carInfoLabel) {
        this.carInfoLabel = new BMap.Label("", { offset: new BMap.Size(18, -60) });
        this.carInfoLabel.setStyle({
          padding: "0",
          backgroundColor: "transparent",
          border: "none",
          pointerEvents: "none"
        });
        this.map.addOverlay(this.carInfoLabel);
      }
      
      const content = `
        <div style="
          background: #fff;
          color: #333;
          padding: 12px 14px;
          border-radius: 8px;
          box-shadow: 0 2px 12px rgba(0,0,0,0.12);
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          font-size: 12px;
          line-height: 1.4;
          width: 220px;
        ">
          <div style="margin-bottom: 8px; padding-bottom: 8px; border-bottom: 1px solid #f0f0f0;">
            <span style="color: #1890ff; font-weight: 500; font-size: 12px;">#${currentIdx} / ${totalCount}</span>
          </div>
          <div style="display: flex; gap: 12px; margin-bottom: 8px;">
            <div><span style="color: #999;">方向</span> <span style="color: #333;">${directionText}</span></div>
            <div><span style="color: #999;">状态</span> <span style="color: #52c41a;">${status}</span></div>
          </div>
          <div style="margin-bottom: 6px;">
            <div style="color: #999; font-size: 11px; margin-bottom: 2px;">时间</div>
            <div style="color: #333;">${dateTime}</div>
          </div>
          <div style="margin-bottom: 6px;">
            <div style="color: #999; font-size: 11px; margin-bottom: 2px;">位置</div>
            <div style="color: #333; word-wrap: break-word; word-break: break-all; white-space: normal;">${location}</div>
          </div>
          <div>
            <div style="color: #999; font-size: 11px; margin-bottom: 2px;">坐标</div>
            <div style="color: #666; font-size: 11px; font-family: Consolas, monospace;">${lng}, ${lat}</div>
          </div>
        </div>
      `;
      
      this.carInfoLabel.setContent(content);
      this.carInfoLabel.setPosition(bmapPoint);
      this.carInfoLabel.show();
    },
    
    hideCarInfoLabel() {
      if (this.carInfoLabel) {
        this.carInfoLabel.hide();
      }
    },
    
    getDirectionText(degree) {
      if (degree === null || degree === undefined) return '-';
      const directions = ['北', '东北', '东', '东南', '南', '西南', '西', '西北'];
      const index = Math.round(degree / 45) % 8;
      return `${directions[index]} ${degree}°`;
    },
    
    showPointInfoWindow(point, bmapPoint, label, color) {
      const dateTime = point.gpsTime ? point.gpsTime.substring(0, 19) : '-';
      const location = point.location || '未知位置';
      const lng = point.lng ? point.lng.toFixed(6) : '-';
      const lat = point.lat ? point.lat.toFixed(6) : '-';
      const direction = point.direction || 0;
      const directionText = this.getDirectionText(direction);
      const status = point.status || '-';
      
      const content = `
        <div style="
          padding: 12px 14px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          font-size: 12px;
          line-height: 1.5;
          min-width: 220px;
        ">
          <div style="margin-bottom: 10px; padding-bottom: 8px; border-bottom: 1px solid #f0f0f0;">
            <span style="background: ${color}; color: #fff; padding: 2px 10px; border-radius: 10px; font-size: 12px; font-weight: 500;">${label}</span>
          </div>
          <div style="display: flex; gap: 16px; margin-bottom: 8px;">
            <div><span style="color: #999;">方向</span> <span style="color: #333;">${directionText}</span></div>
            <div><span style="color: #999;">状态</span> <span style="color: #52c41a;">${status}</span></div>
          </div>
          <div style="margin-bottom: 6px;">
            <div style="color: #999; font-size: 11px;">时间</div>
            <div style="color: #333;">${dateTime}</div>
          </div>
          <div style="margin-bottom: 6px;">
            <div style="color: #999; font-size: 11px;">位置</div>
            <div style="color: #333; word-wrap: break-word;">${location}</div>
          </div>
          <div>
            <div style="color: #999; font-size: 11px;">坐标</div>
            <div style="color: #666; font-family: Consolas, monospace;">${lng}, ${lat}</div>
          </div>
        </div>
      `;
      
      const infoWindow = new BMap.InfoWindow(content, {
        width: 260,
        height: 0,
        title: "",
        enableCloseOnClick: true
      });
      this.map.openInfoWindow(infoWindow, bmapPoint);
    },

    updateCurrentPointInfo() {
      if (!this.trackData || !this.trackData.pathPoints) {
        this.currentPointInfo = null;
        return;
      }
      const points = this.trackData.pathPoints;
      if (this.currentIndex >= 0 && this.currentIndex < points.length) {
        this.currentPointInfo = points[this.currentIndex];
      }
    },

    changeSpeed(speed) {
      this.playSpeed = speed;
    },

    seekTo(index) {
      const idx = parseInt(index, 10);
      this.currentIndex = idx;
      this.moveCarToIndex(idx);
    }
  }
};
</script>

<style lang="less" scoped>
@primary: #1890ff;
@success: #52c41a;
@danger: #f5222d;
@bg: #f5f7fa;
@card: #fff;
@border: #e8e8e8;
@text: #333;
@text-muted: #999;

.track-page {
  height: calc(100vh - 80px); // 减去顶部导航高度
  width: 100%;
  display: flex;
  flex-direction: column;
  background: @bg;
  padding: 12px;
  box-sizing: border-box;
  gap: 12px;
  overflow: hidden;
}

// 搜索栏
.search-bar {
  background: @card;
  border-radius: 4px;
  padding: 12px 16px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.08);
  flex-shrink: 0;
  position: relative;
  z-index: 10;

  .search-inner {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .search-item {
    display: flex;
    align-items: center;
    gap: 8px;

    .label {
      color: @text-muted;
      font-size: 13px;
      white-space: nowrap;
    }
  }
}

// 主内容
.main-content {
  flex: 1;
  display: flex;
  gap: 12px;
  min-height: 0;
  overflow: hidden;
}

// 左侧面板
.left-panel {
  width: 280px;
  flex-shrink: 0;
  background: @card;
  border-radius: 4px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.08);
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow: hidden;

  &.empty {
    justify-content: center;
    align-items: center;
    .empty-icon i {
      font-size: 48px;
      color: #ddd;
    }
    p {
      color: @text-muted;
      margin-top: 12px;
      font-size: 13px;
    }
  }
}

.panel-section {
  flex-shrink: 0;
  
  .panel-header {
    display: flex;
    align-items: center;
    gap: 6px;
    color: @text;
    font-size: 13px;
    font-weight: 500;
    margin-bottom: 10px;
    i { color: @primary; }
    .count {
      margin-left: auto;
      color: @text-muted;
      font-size: 12px;
    }
  }
  
  &.track-list-section {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
}

.stats-row {
  display: flex;
  gap: 8px;
  .stat-item {
    flex: 1;
    background: @bg;
    border-radius: 4px;
    padding: 10px 8px;
    text-align: center;
    .stat-value {
      display: block;
      color: @primary;
      font-size: 14px;
      font-weight: 600;
    }
    .stat-label {
      display: block;
      color: @text-muted;
      font-size: 11px;
      margin-top: 4px;
    }
  }
}

// 播放控制优化样式
.playback-section {
  .panel-header {
    .playback-status {
      margin-left: auto;
      font-size: 11px;
      font-weight: 400;
      padding: 2px 8px;
      border-radius: 10px;
      background: #f0f0f0;
      color: @text-muted;
      transition: all 0.3s;
      
      &.playing {
        background: linear-gradient(135deg, #52c41a 0%, #73d13d 100%);
        color: white;
        animation: pulse 1.5s infinite;
      }
    }
  }
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

.main-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 16px;
  padding: 12px 0;
}

.play-btn {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: linear-gradient(135deg, @primary 0%, #4080ff 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(45, 140, 240, 0.4);
  
  i {
    color: white;
    font-size: 22px;
    margin-left: 2px;
  }
  
  &:hover {
    transform: scale(1.08);
    box-shadow: 0 6px 20px rgba(45, 140, 240, 0.5);
  }
  
  &:active {
    transform: scale(0.96);
  }
  
  &.playing {
    background: linear-gradient(135deg, #ff4d4f 0%, #ff7875 100%);
    box-shadow: 0 4px 12px rgba(255, 77, 79, 0.4);
    
    i { margin-left: 0; }
    
    &:hover {
      box-shadow: 0 6px 20px rgba(255, 77, 79, 0.5);
    }
  }
}

.control-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: @bg;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid #e8e8e8;
  
  i {
    color: @text-muted;
    font-size: 16px;
  }
  
  &:hover {
    background: #e6e6e6;
    i { color: @text; }
  }
}

// 进度条样式
.progress-wrapper {
  margin-bottom: 16px;
}

.progress-track {
  position: relative;
  height: 6px;
  background: #e8e8e8;
  border-radius: 3px;
  overflow: visible;
}

.progress-fill {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background: linear-gradient(90deg, @primary 0%, #69b1ff 100%);
  border-radius: 3px;
  transition: width 0.1s linear;
}

.progress-slider {
  position: absolute;
  top: -7px;
  left: 0;
  width: 100%;
  height: 20px;
  opacity: 0;
  cursor: pointer;
  z-index: 2;
  -webkit-appearance: none;
  appearance: none;
  
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: @primary;
    cursor: pointer;
  }
}

.progress-info {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin-top: 8px;
  
  .progress-current {
    font-size: 16px;
    font-weight: 600;
    color: @primary;
  }
  
  .progress-divider {
    color: #d9d9d9;
    font-size: 12px;
  }
  
  .progress-total {
    font-size: 12px;
    color: @text-muted;
  }
}

// 速度选择
.speed-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding: 0 4px;
}

.speed-label {
  font-size: 12px;
  color: @text-muted;
}

.speed-options {
  display: flex;
  gap: 6px;
}

.speed-chip {
  min-width: 36px;
  height: 26px;
  padding: 0 8px;
  border-radius: 13px;
  background: @bg;
  border: 1px solid #e8e8e8;
  font-size: 11px;
  font-weight: 500;
  color: @text-muted;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    border-color: @primary;
    color: @primary;
  }
  
  &.active {
    background: linear-gradient(135deg, @primary 0%, #4080ff 100%);
    border-color: transparent;
    color: white;
    box-shadow: 0 2px 6px rgba(45, 140, 240, 0.3);
  }
}

// 当前点信息卡片
.current-info-card {
  background: linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%);
  border-radius: 8px;
  padding: 12px;
  border: 1px solid #e8e8e8;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 10px;
  
  &.speed {
    .info-icon {
      background: linear-gradient(135deg, @primary 0%, #4080ff 100%);
      i { color: white; }
    }
  }
  
  &.location {
    .info-icon {
      background: linear-gradient(135deg, #52c41a 0%, #73d13d 100%);
      i { color: white; }
    }
  }
}

.info-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: @bg;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  i {
    font-size: 16px;
    color: @text-muted;
  }
}

.info-data {
  display: flex;
  align-items: baseline;
  gap: 4px;
  
  .value {
    font-size: 20px;
    font-weight: 600;
    color: @text;
  }
  
  .unit {
    font-size: 12px;
    color: @text-muted;
  }
}

.info-divider {
  height: 1px;
  background: #e8e8e8;
  margin: 10px 0;
}

.info-text {
  flex: 1;
  font-size: 12px;
  color: @text-muted;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
}

// 轨迹列表
.track-list-body {
  flex: 1;
  min-height: 0;
  overflow-y: scroll; // 始终显示滚动条，避免晃动
  overflow-x: hidden;
  background: @bg;
  border-radius: 4px;
  padding: 4px;
  padding-right: 8px; // 给滚动条留空间

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: #eee;
    border-radius: 2px;
  }
  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 2px;
  }
}

.track-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 4px;
  margin-bottom: 4px;
  background: @card;
  transition: all 0.2s;

  &.active {
    background: fade(@primary, 10%);
    border-left: 2px solid @primary;
  }

  .item-index {
    width: 20px;
    height: 20px;
    background: @bg;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: @text-muted;
    font-size: 10px;
    flex-shrink: 0;
  }

  .item-content {
    flex: 1;
    min-width: 0;
    .item-main {
      display: flex;
      align-items: center;
      gap: 8px;
      .item-time {
        color: @text;
        font-size: 12px;
      }
      .item-speed {
        color: @primary;
        font-size: 11px;
      }
    }
    .item-location {
      color: @text-muted;
      font-size: 11px;
      margin-top: 2px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}

// 右侧地图区域
.map-area {
  flex: 1;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0,0,0,0.08);
  min-height: 0;
  
  .map-container {
    width: 100%;
    height: 100%;
  }
}
</style>

<style lang="less">
// 全局样式
</style>
